import type { RelationshipType } from '../types/relationship'

import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, nextTick, ref } from 'vue'

import { useProvidersStore } from './providers'

export const ONBOARDING_VERSION = 2

export const useOnboardingStore = defineStore('onboarding', () => {
  const providersStore = useProvidersStore()

  const hasCompletedSetup = useLocalStorage('onboarding/completed', false)
  const hasSkippedSetup = useLocalStorage('onboarding/skipped', false)
  const onboardingVersion = useLocalStorage('onboarding/version', 0)

  // 用户在引导中设置的默认初始好感度和关系类型
  const defaultInitialAffection = useLocalStorage('onboarding/default-initial-affection', 40)
  const defaultInitialRelationType = useLocalStorage<RelationshipType>('onboarding/default-initial-relation-type', 'friend')

  const shouldShowSetup = ref(false)

  const hasEssentialProviderConfigured = computed(() => {
    const essentialProviders = ['neko-teaparty', 'openai', 'anthropic', 'google-generative-ai', 'openrouter-ai', 'ollama', 'deepseek', 'openai-compatible']
    return essentialProviders.some(providerId => providersStore.configuredProviders[providerId])
  })

  const needsOnboarding = computed(() => {
    // 旧版引导已完成的用户不打扰
    if (hasCompletedSetup.value || hasSkippedSetup.value) {
      if (import.meta.env.DEV) {
        console.info('ℹ️ Onboarding already completed or skipped')
      }
      return false
    }

    if (hasEssentialProviderConfigured.value) {
      if (import.meta.env.DEV) {
        console.info('ℹ️ Essential provider already configured, no onboarding needed')
      }
      return false
    }

    return true
  })

  async function initializeSetupCheck(hasExistingContacts = false) {
    if (hasExistingContacts) {
      markSetupSkipped()
      return
    }

    if (needsOnboarding.value) {
      await nextTick()
      shouldShowSetup.value = true
    }
  }

  function markSetupCompleted() {
    hasCompletedSetup.value = true
    hasSkippedSetup.value = false
    shouldShowSetup.value = false
    onboardingVersion.value = ONBOARDING_VERSION
  }

  function markSetupSkipped() {
    hasSkippedSetup.value = true
    shouldShowSetup.value = false
    onboardingVersion.value = ONBOARDING_VERSION
  }

  function resetSetupState() {
    hasCompletedSetup.value = false
    hasSkippedSetup.value = false
    onboardingVersion.value = 0
    shouldShowSetup.value = false
  }

  function forceShowSetup() {
    shouldShowSetup.value = true
  }

  return {
    hasCompletedSetup,
    hasSkippedSetup,
    shouldShowSetup,
    hasEssentialProviderConfigured,
    needsOnboarding,
    onboardingVersion,
    defaultInitialAffection,
    defaultInitialRelationType,

    initializeSetupCheck,
    markSetupCompleted,
    markSetupSkipped,
    resetSetupState,
    forceShowSetup,
  }
})
